import { useEffect } from "react";

export interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown> | Record<string, unknown>[];
  hreflang?: { lang: string; url: string }[];
  noindex?: boolean;
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let meta = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;

  let link = document.head.querySelector(selector) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
  if (hreflang) {
    link.setAttribute("hreflang", hreflang);
  }
}

function setJsonLd(schema: SEOHeadProps["schema"]) {
  const previous = document.head.querySelectorAll("script[data-owl-jsonld='true']");
  previous.forEach((node) => node.remove());

  if (!schema) {
    return;
  }

  const payload = Array.isArray(schema) ? schema : [schema];
  payload.forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.owlJsonld = "true";
    script.text = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

export default function SEOHead({
  title,
  description,
  canonicalUrl,
  ogImage = "https://www.obsidianweblabs.com/owl-mark.svg",
  ogType = "website",
  schema,
  hreflang,
  noindex = false,
}: SEOHeadProps) {
  // Normalize canonical to always use www. prefix for consistency with sitemap
  const normalizedCanonical = canonicalUrl.replace(
    'https://obsidianweblabs.com',
    'https://www.obsidianweblabs.com'
  );

  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", normalizedCanonical);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:image", ogImage);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", ogImage);

    upsertLink("canonical", normalizedCanonical);

    const previousHreflangs = document.head.querySelectorAll("link[rel='alternate'][hreflang]");
    previousHreflangs.forEach((node) => node.remove());
    hreflang?.forEach((entry) => {
      upsertLink("alternate", entry.url, entry.lang);
    });

    setJsonLd(schema);
  }, [title, description, canonicalUrl, ogImage, ogType, schema, hreflang, noindex]);

  return null;
}
